import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, FileText, Loader2 } from "lucide-react";
import { listsUrl, fetchSession, fetchBF, fetchBFAgent, logo, pingCart, trackBF, vid, fetchAgentCart, saveAgentCart, fetchCartRev } from "@/lib/bf";
import BFMyOrders from "./BFMyOrders";
import Countdown from "./Countdown";
import BFGdprGate, { isRegistered, saveReg } from "./BFGdprGate";
import { BrandView, BrandsZone, CategoryView, TopButton } from "./BFZones";
import BFYours from "./BFYours";
import Cart from "./Cart";

const leadId = () => {
  const s = new URLSearchParams(window.location.search).get("s");
  if (s && /^[a-z0-9]{6,24}$/i.test(s)) localStorage.setItem("bf_lead_id", s);
  return localStorage.getItem("bf_lead_id") || "";
};

const agentSlug = () => {
  const a = new URLSearchParams(window.location.search).get("a");
  if (a && /^[a-z0-9]{4,24}$/i.test(a)) localStorage.setItem("bf_agent", a);
  return localStorage.getItem("bf_agent") || "";
};

if (new URLSearchParams(window.location.search).get("reset") === "1") ["bf_reg", "bf_lead_id", "bf_reg_info", "bf_cart", "bf_lead"].forEach((k) => localStorage.removeItem(k));

const editLead = (() => { const e = new URLSearchParams(window.location.search).get("edit") || ""; return /^[a-z0-9]{4,24}$/i.test(e) ? e : ""; })();

const keysOf = (cart) => Object.entries(cart).filter(([k, q]) => q > 0 && k.startsWith("cp:")).map(([key, q]) => ({ key, q }));

const useCart = (preview) => {
  const [cart, setCart] = useState(() => (editLead ? {} : JSON.parse(localStorage.getItem("bf_cart") || "{}")));
  const [info, setInfo] = useState(editLead ? null : {});
  const [synced, setSynced] = useState(!!editLead || preview);
  const [updated, setUpdated] = useState(false);
  const [noMin, setNoMin] = useState(false);
  const frozen = useRef(false);
  useEffect(() => {
    if (editLead) {
      fetchAgentCart(editLead).then((d) => { setCart(Object.fromEntries(d.lines.map((l) => [l.key, l.q]))); setInfo(d); }).catch(() => setInfo(false));
      return;
    }
    const lead = localStorage.getItem("bf_lead_id");
    if (preview || !lead) { setSynced(true); return; }
    fetchCartRev(lead).then((d) => {
      if (d.no_min) setNoMin(true);
      if (d.rev && d.rev > (localStorage.getItem("bf_cart_rev") || "")) {
        localStorage.setItem("bf_cart_rev", d.rev);
        if (localStorage.getItem("bf_cart_rev_seen")) setUpdated((d.lines || []).length ? "edit" : "done");
        setCart(Object.fromEntries((d.lines || []).map((l) => [l.key, l.q])));
      }
      localStorage.setItem("bf_cart_rev_seen", "1");
    }).catch(() => {}).finally(() => setSynced(true));
  }, [preview]);
  useEffect(() => { if (!editLead) localStorage.setItem("bf_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => {
    if (editLead) {
      if (!info || frozen.current) return undefined;
      const t = setTimeout(() => !frozen.current && saveAgentCart(editLead, keysOf(cart)).catch(() => {}), 1200);
      return () => clearTimeout(t);
    }
    const lead = localStorage.getItem("bf_lead_id");
    if (preview || !synced || !lead || !isRegistered()) return undefined;
    const t = setTimeout(() => pingCart({ lead, agent: localStorage.getItem("bf_agent") || "", items: keysOf(cart) }), 3000);
    return () => clearTimeout(t);
  }, [cart, preview, info, synced]);
  const setQty = useCallback((id, q) => setCart((c) => ({ ...c, [id]: Math.max(0, Math.min(999, q)) })), []);
  return [cart, setQty, () => setCart({}), info, updated, () => { frozen.current = true; }, noMin || !!info?.no_min];
};

const trail = [window.location.hash.slice(1)];
const useHash = () => {
  const [h, setH] = useState(() => window.location.hash.slice(1));
  useEffect(() => {
    const on = () => {
      const now = window.location.hash.slice(1);
      if (trail[trail.length - 2] === now) trail.pop(); else trail.push(now);
      setH(now);
      window.scrollTo({ top: 0 });
    };
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  const go = useCallback((to) => {
    if (trail.length > 1 && trail[trail.length - 2] === to) window.history.back();
    else if (to) window.location.hash = to;
    else window.history.pushState(null, "", window.location.pathname + window.location.search) || window.dispatchEvent(new HashChangeEvent("hashchange"));
  }, []);
  return [h, go];
};

export default function BlackFriday() {
  const [reg, setReg] = useState(isRegistered);
  const [sess, setSess] = useState(() => !new URLSearchParams(window.location.search).get("s") || isRegistered());
  useEffect(() => {
    if (sess) return;
    fetchSession(new URLSearchParams(window.location.search).get("s"))
      .then(({ id, ...info }) => { saveReg(id, info); setReg(true); }).catch(() => {}).finally(() => setSess(true));
  }, [sess]);
  const [cfg, setCfg] = useState(null);
  const [hash, go] = useHash();
  const [agent, setAgent] = useState(null);
  useEffect(() => { const a = agentSlug(); if (a) fetchBFAgent(a).then(setAgent).catch((e) => e.response?.status === 404 && localStorage.removeItem("bf_agent")); }, []);
  const previewKey = new URLSearchParams(window.location.search).get("preview");
  const preview = previewKey !== null || !!editLead;
  const [cart, setQty, clear, editInfo, updated, freeze, noMin] = useCart(preview);
  const demo = !!editLead || (preview && new URLSearchParams(window.location.search).get("demo") === "1");
  const track = useCallback((kind, target = "", total = 0, parts = [], lines = [], proforma = "", notes = "") => !preview && trackBF({ visitor_id: vid(), kind, target, total, parts, lines, proforma, notes, lead: leadId(), agent: localStorage.getItem("bf_agent") || "" }), [preview]);
  useEffect(() => {
    document.title = "Black Friday · Beauty Trends 2026";
    leadId();
    fetchBF(previewKey).then((c) => { setCfg(c); if (c.published) track("visit"); }).catch((e) => setCfg(e.response?.status === 404 ? { ...e.response.data, soon: true } : false));
  }, [track, previewKey]);
  const [, brand, cat] = hash.startsWith("b/") ? hash.split("/") : [];
  const valid = cfg?.brands?.some((b) => b.id === brand);
  useEffect(() => { if (valid) track("brand", brand); }, [valid, brand, track]);
  if (cfg === null || !sess || editInfo === null) return <div className="bf-app bf-loading"><Loader2 className="animate-spin" /></div>;
  if (editInfo === false) return <div className="bf-app bf-loading" data-testid="bf-edit-denied">Nu poți deschide coșul acestui salon.</div>;
  if (cfg === false) return <div className="bf-app bf-loading">Pagina nu poate fi încărcată acum.</div>;
  if (cfg && cfg.soon) return <BFGdprGate cfg={cfg} soon />;
  if (!reg && !demo) return <BFGdprGate cfg={cfg} preview={!!preview} onDone={() => { setReg(true); window.scrollTo(0, 0); }} />;
  const contact = { phone: agent?.phone || cfg?.whatsapp || "", name: agent?.phone ? agent.name : "" };
  const hasCat = valid && cat && cfg.campaigns?.some((c) => c.brand === brand && c.category === cat);
  const offer = valid && !cat && cfg.campaigns?.some((c) => c.brand === brand && c.category === "oferta");
  const blondie = brand === "medavita" && cat === "decolorare";
  const view = hasCat ? <CategoryView cfg={cfg} brand={brand} cat={cat} go={go} cart={cart} setQty={setQty} contact={contact} onWant={(id) => track("want", id)} />
    : valid ? <BrandView cfg={cfg} brand={brand} go={go} cart={cart} setQty={setQty} />
    : hash === "branduri" ? <BrandsZone cfg={cfg} go={go} />
    : hash === "castig" ? <BFYours cfg={cfg} go={go} cart={cart} /> : null;
  return (
    <div className="bf-app" data-testid="bf-page">
      {editLead && <div className="bf-edit-bar" data-testid="bf-edit-bar">Modifici coșul salonului <b>{editInfo.salon || "Salon"}</b> · se salvează automat și salonul vede modificările. <a href="/?s=cosuri" data-testid="bf-edit-back">Înapoi la coșuri</a></div>}
      {updated && <div className="bf-edit-bar bf-edit-bar--salon" data-testid="bf-cart-updated">{updated === "done" ? "Agentul tău a făcut proforma pentru comanda ta. O primești pe e-mail și pe WhatsApp." : "Agentul tău a actualizat coșul. Verifică-l și trimite comanda când ești gata."}</div>}
      {!cfg.published && !editLead && <div className="pub-preview" data-testid="bf-preview-banner">Previzualizare privată · pagina nu e publicată, doar tu o vezi</div>}
      {view ? (
        <>
          <button type="button" className="bf-mini-head" onClick={() => go("")} data-testid="bf-home-link"><b>{cfg.title}</b> <span>{cfg.subtitle}</span></button>
          {(hasCat || offer) && <img className={`bf-watermark ${blondie ? "bf-watermark--blondie" : ""}`} src={blondie ? "/blondie-logo.png" : logo(brand)} alt="" aria-hidden="true" data-testid="bf-watermark" />}
          <main className="bf-offers bf-offers--front">{view}</main>
        </>
      ) : (
        <header className="bf-hero bf-hero--home">
          <div className="bf-hero-mark" aria-hidden="true" data-testid="bf-hero-watermark">
            <img src="/brands/vs_beautytrends_w.png" alt="" />
            <span>BF 2026</span>
          </div>
          <span className="bf-kicker">Beauty Trends · ofertă pentru saloane</span>
          <h1 data-testid="bf-title">{cfg.title}</h1>
          <p className="bf-sub" data-testid="bf-subtitle">{cfg.subtitle}</p>
          <p className="bf-intro">{cfg.intro}</p>
          <Countdown cfg={cfg} />
          <TopButton onOpen={() => go("branduri")} />
          <button type="button" className="bf-yours-btn" onClick={() => go("castig")} data-testid="bf-yours-open">Black Friday-ul tău · cât câștigi revânzând <ArrowRight size={16} /></button>
          <a className="bf-lists-btn" href={listsUrl()} target="_blank" rel="noopener noreferrer" data-testid="bf-lists-open"><FileText size={16} /> Liste de prețuri (PDF / Excel)</a>
        </header>
      )}
      <footer className="bf-foot">
        <span data-testid="bf-foot-contact">{contact.name || cfg.contact}{contact.phone ? ` · ${contact.phone}` : ""}</span>
        <span>Ofertă valabilă în limita stocului disponibil, primul venit primul servit. Prețuri cu TVA inclus.</span>
      </footer>
      {!editLead && <BFMyOrders preview={preview} />}
      <Cart cfg={cfg} contact={contact} cart={cart} setQty={setQty} clear={clear} preview={preview} noMin={noMin} edit={editLead ? { ...editInfo, freeze } : null} onOrder={(total, parts, lines, pf, notes) => track("order", "", total, parts, lines, pf, notes)} />
    </div>
  );
}
